import React, { useState, useMemo } from 'react'

import { useLocation } from "react-router-dom"
import { useQueryClient } from '@tanstack/react-query';
import SOURCES from '../../../../utils/constants';
import CommonCard from '../../../global/card';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import useDebounce from '../../../../utils/customHooks';
import EnhancedLeadsImportedViaFormList from './EnhancedLeadsImportedViaFormList';
import CommonPagination from '../../../global/pagination';
import CommonSkeletonLoader from '../../../global/loading';

const EnhancedLeadsImportedViaForm = () => {
    const queryClient = useQueryClient();
    const [searchLeadsImportedViaForm, setSearchLeadsImportedViaForm] = useState("");

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        10
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);
    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
    const [leadsImportedViaFormViewType, setLeadsImportedViaFormViewType] = useState(() => {
        return localStorage.getItem("leadsImportedViaFormViewType") || "GRID";
    });
    const debouncedSearchTerm = useDebounce(searchLeadsImportedViaForm, 500);


    const keyParams = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };


    const { data: leadsData, isLoading: leadsLoading, isFetching: leadsFetching } = useGenericQuery({
        endpoint: "weblink/list/leads",
        queryKey: QUERY_KEYS.leadsImportedViaForm.lists({ ...keyParams }),
        queryParams: { ...keyParams },
        onError: () => { },
        onSuccess: () => { },
    })

    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "weblink/lead-search",
        queryKey: QUERY_KEYS.leadsImportedViaForm.lists({ q: debouncedSearchTerm }),
        queryParams: { q: debouncedSearchTerm },
        enabled: !!debouncedSearchTerm
    })

    const leadAcceptMutation = useGenericMutation({
        endpoint: "weblink/lead/process",
    });

    const handleAcceptReject = async (leadId, type = 'ACCEPT') => {
        await leadAcceptMutation.mutateAsync({ leadId: leadId, type: type });
        // Invalidate queries to refresh list
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leadsImportedViaForm.lists({ ...keyParams }) });
        if (debouncedSearchTerm) {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leadsImportedViaForm.lists({ q: debouncedSearchTerm }) });
        }
    };

    // Determine final data to display
    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm) {
            return searchData?.results || [];
        }
        return leadsData?.results || [];
    }, [leadsData, searchData, debouncedSearchTerm]);

    const isLoading = leadsLoading || searchDataLoading;
    const dataExist = getFinalData && getFinalData.length > 0;
    const totalRecords = debouncedSearchTerm ? searchData?.totalReords : leadsData?.totalReords;

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search leads imported via form...",
                searchInput: searchLeadsImportedViaForm,
                setSearchInput: setSearchLeadsImportedViaForm
            }}
            isListGridView={{
                show: true,
                viewType: leadsImportedViaFormViewType,
                setViewType: setLeadsImportedViaFormViewType,
                viewFor: "leadsImportedViaFormViewType"
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        type={leadsImportedViaFormViewType === "GRID" ? "card" : "table"}
                    />
                ),
            }}
            className={(!isLoading && leadsFetching) && "opacity-50"}
        >
            <EnhancedLeadsImportedViaFormList
                isLoading={isLoading}
                leadsImported={getFinalData}
                sort={sort}
                setSort={setSort}
                viewType={leadsImportedViaFormViewType}
                handleAcceptReject={handleAcceptReject}
                leadAcceptMutation={leadAcceptMutation}
            />

            {(dataExist && !debouncedSearchTerm) &&
                totalRecords > SOURCES.DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={totalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={getFinalData?.length || 0}
                        paginationFor="Leads Imported Via Form"
                        className="mt-4"
                    />
                )}
        </CommonCard>
    )
}

export default EnhancedLeadsImportedViaForm